import { Link, isRouteErrorResponse, useRouteError } from 'react-router';
import { Button, buttonClassName } from '@ircf/ui';
import { brand } from '@ircf/config';
import { ApiError } from '../lib/api/client.js';

/**
 * Top-level route error boundary.
 *
 * Deliberately conservative about what it shows. A production visitor gets a
 * plain explanation and a way out; the stack and message are only rendered in
 * development, because an API error message can legitimately contain a record
 * identifier or a validation detail that has no business being on screen.
 *
 * A stale-chunk failure is handled separately: after a redeploy an old tab holds
 * hashed chunk URLs that no longer exist, and the only real fix is a reload.
 */
export function RouteError() {
  const error = useRouteError();

  const isChunkFailure =
    error instanceof Error &&
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
      error.message,
    );

  if (isChunkFailure) {
    return (
      <Shell
        heading="This page needs a refresh"
        body={`${brand.businessName} has been updated since you opened this tab. Reload to pick up the new version.`}
      >
        <Button onClick={() => window.location.reload()}>Reload the page</Button>
      </Shell>
    );
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <Shell
        heading="Page not found"
        body="That address does not match any page on this site. It may have moved."
      >
        <Link to="/" className={buttonClassName({ variant: 'primary' })}>
          Return to the homepage
        </Link>
      </Shell>
    );
  }

  const isPermission = error instanceof ApiError && (error.status === 403 || error.status === 401);

  if (isPermission) {
    return (
      <Shell
        heading="You do not have access to this page"
        body="Your account is signed in but is not permitted to view this area. If you believe this is wrong, contact your coach."
      >
        <Link to="/" className={buttonClassName({ variant: 'secondary' })}>
          Return to the homepage
        </Link>
      </Shell>
    );
  }

  return (
    <Shell
      heading="Something went wrong"
      body="An unexpected error stopped this page from loading. Your data has not been changed."
    >
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => window.location.reload()}>Try again</Button>
        <Link to="/" className={buttonClassName({ variant: 'secondary' })}>
          Return to the homepage
        </Link>
      </div>

      {import.meta.env.DEV && error instanceof Error ? (
        <details className="mt-8 w-full max-w-2xl rounded-lg border border-white/10 bg-graphite/60 p-4 text-left">
          <summary className="cursor-pointer text-sm font-medium text-platinum">
            Developer detail (development builds only)
          </summary>
          <pre className="mt-3 overflow-x-auto text-xs leading-relaxed text-cool-steel">
            {error.stack ?? error.message}
          </pre>
        </details>
      ) : null}
    </Shell>
  );
}

function Shell({
  heading,
  body,
  children,
}: {
  heading: string;
  body: string;
  children: React.ReactNode;
}) {
  return (
    <main
      id="main"
      className="flex min-h-screen flex-col items-center justify-center gap-6 bg-near-black px-6 py-24 text-center"
    >
      <div className="max-w-xl space-y-4">
        <h1 className="text-3xl font-semibold text-warm-white sm:text-4xl">{heading}</h1>
        <p className="text-base leading-relaxed text-cool-steel">{body}</p>
      </div>
      {children}
    </main>
  );
}
